import type { ComponentProps, ReactNode } from 'react';
import { PanelRightClose, PanelRightOpen } from 'lucide-react';
import { useI18n } from '@/shared/i18n';
import { cn } from '@/shared/lib/utils';
import { Button } from '@/shared/ui/shadcn/button';
import { SidebarTrigger } from '@/shared/ui/shadcn/sidebar';

function BarDivider() {
  return <span aria-hidden className="h-4 w-px shrink-0 self-center bg-border" />;
}

export function HeaderAction({
  label,
  className,
  children,
  ...props
}: ComponentProps<typeof Button> & { label: string }) {
  return (
    <Button
      type="button"
      size="icon-sm"
      variant="ghost"
      aria-label={label}
      title={label}
      className={className}
      {...props}
    >
      {children}
    </Button>
  );
}

export function PageHeader({
  title,
  leading,
  leadingMode = 'mobile',
  actions,
  sidebarOpen = false,
  onToggleSidebar,
}: {
  title: ReactNode;
  /** Accepted so existing pages can keep passing a subtitle; the bar shows the section name only. */
  description?: ReactNode;
  leading?: ReactNode;
  /** `mobile` keeps the chats split-view back control off landscape; detail pages use `always`. */
  leadingMode?: 'mobile' | 'always';
  actions?: ReactNode;
  sidebarOpen?: boolean;
  /** Toggles the section's right sidebar. Portrait overlays it; landscape docks it. */
  onToggleSidebar?: () => void;
}) {
  const { t } = useI18n();
  return (
    <header className="flex h-14 shrink-0 items-center gap-2 border-b px-3 desktop:px-4">
      <SidebarTrigger className="-ml-1 shrink-0" />
      <BarDivider />
      {leading ? (
        <div
          className={cn(
            'flex shrink-0 items-center',
            leadingMode === 'mobile' && 'desktop:hidden',
          )}
        >
          {leading}
        </div>
      ) : null}
      <div className="min-w-0 flex-1">
        {typeof title === 'string' || typeof title === 'number' ? (
          <h1 className="truncate text-sm font-semibold">{title}</h1>
        ) : (
          title
        )}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-1">{actions}</div> : null}
      {onToggleSidebar ? (
        <div className="flex shrink-0 items-center gap-2">
          <BarDivider />
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={t.common.panel}
            aria-pressed={sidebarOpen}
            onClick={onToggleSidebar}
          >
            {sidebarOpen ? (
              <PanelRightClose className="size-4" />
            ) : (
              <PanelRightOpen className="size-4" />
            )}
          </Button>
        </div>
      ) : null}
    </header>
  );
}
